import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useNavigate, useParams } from 'react-router';
import classNames from 'classnames';
import _ from 'lodash';

import { ProtectedButton } from '../shared/ProtectedButton';
import { QAWebToolButton } from '../shared/QAWebToolButton';
import { POST_TEMPLATES } from './model/PostTemplates';
import { isPostData, PostData } from './model/PostTypes';
import { PortalTranslationManager } from './PortalTranslationManager';
import { PostMetaFields } from './PostMetaFields';
import { PostTemplateInput } from './templates/PostTemplateInput';
import { ValidationErrors } from './ValidationErrors';
import { Loading, Section } from '../shared';
import {
    isTemplateId,
    PORTAL_POST_ID_MAX_LENGTH,
    PortalPostState,
    TemplateId,
    UserRoles,
} from '@lifeserver/shared';

import {
    PortalLocalizationDTO,
    PortalPostLocalizationDTO,
    Post as PostType,
    useCreatePortalPost,
    usePortalLocalization,
    usePortalPost,
    usePortalTags,
    useSavePortalLocalization,
    useUpdatePortalPost,
} from '../hooks/portal';
import { usePreviewPortalPayload } from '../hooks/usePreviewPortalPayload';
import { useUrlFlags } from '../hooks/useUrlFlags';

import { assert } from '../utils/assert';
import { handleError } from '../utils/utils';

import { RouteTo } from '../routes';

import styles from './Post.module.scss';
import {
    pruneOrphanedLocalizations,
    pruneStalePostLocalizations,
} from './translationUtils';

import { URL_FLAGS } from '../constants/urlFlags';
import { MediaProcessingReporterContext } from './templates/inputs/mediaProcessingReporter';
import {
    APP_VER_REGEX,
    hasErrors,
    POST_VALIDATIONS,
    validatePostData,
} from './templates/validators';

export interface PortalPostEditorContextData {
    post: PostData;
    validations: (typeof POST_VALIDATIONS)[TemplateId];
    errors: Record<string, unknown>;
    hasChanges: boolean;
    isEditingExistingPost: boolean;
    postState: PortalPostState;
    initialPostState?: PortalPostState;
    localizations: PortalLocalizationDTO[];
    postId: string;
    localizationRecord: Record<string, Record<string, string>>;
    setLocalizations: (localizations: PortalLocalizationDTO[]) => void;
    postLocalizations: PortalPostLocalizationDTO[];
    setPostLocalizations: (
        postLocalizations: PortalPostLocalizationDTO[],
    ) => void;
}

export const POST_STATE_COLOR_MAP: Record<PortalPostState, string> = {
    todo: '#F1F3F3',
    in_progress: '#F0AD4E',
    in_review: '#1868FF',
    ready: '#20882E',
};

export const POST_STATE_DISPLAY_MAP: Record<PortalPostState, string> = {
    todo: 'To Do',
    in_progress: 'In Progress',
    in_review: 'In Review',
    ready: 'Ready',
};

export const PortalPostEditorContext =
    createContext<PortalPostEditorContextData | null>(null);

export function Post() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: post, error: postError } = usePortalPost(id);
    const { mutate: createPost, isPending: isCreating } = useCreatePortalPost();
    const { mutate: updatePost, isPending: isUpdating } = useUpdatePortalPost();

    // Redirect to the list view if fetching failed
    useEffect(() => {
        if (postError) {
            handleError('Failed to fetch the post', postError);
            navigate(RouteTo.PortalLibrary());
        }
    }, [postError]);

    if (id && !post) {
        return <Loading />;
    }

    return (
        <PostEditor
            id={id}
            post={post}
            createPost={createPost}
            updatePost={updatePost}
            isSaving={isCreating || isUpdating}
        />
    );
}

type PostEditorProps = {
    id?: string;
    post?: PostType;
    createPost: ReturnType<typeof useCreatePortalPost>['mutate'];
    updatePost: ReturnType<typeof useUpdatePortalPost>['mutate'];
    isSaving?: boolean;
};

function PostEditor(props: PostEditorProps) {
    const { id, post, createPost, updatePost } = props;

    const navigate = useNavigate();
    const { isUrlFlagEnabled } = useUrlFlags();
    const skipValidationBlock = isUrlFlagEnabled(
        URL_FLAGS.SKIP_VALIDATION_BLOCK,
    );
    const [notes, setNotes] = useState(post?.notes ?? '');
    const [postState, setPostState] = useState<PortalPostState>(
        post?.state ?? 'in_progress',
    );
    const [draftId, setDraftId] = useState(id ?? '');
    const [draftTemplateId, setDraftTemplateId] = useState<TemplateId>(
        isTemplateId(post?.templateId)
            ? post.templateId
            : 'article-static-medium',
    );
    const [postPayload, setPostPayload] = useState<PostData>(
        isPostData(post?.payload)
            ? post.payload
            : POST_TEMPLATES[draftTemplateId].emptyData(),
    );
    const [initialPostPayload, setInitialPostPayload] = useState<
        PostData | undefined
    >(isPostData(post?.payload) ? post.payload : undefined);

    const [initialTags, setInitialTags] = useState<Array<{ name: string }>>(
        post?.tags?.map((pt) => ({ name: pt.name })) ?? [],
    );

    const [postLocalizations, setPostLocalizations] = useState<
        PortalPostLocalizationDTO[]
    >(post?.postLocalizations ?? []);

    const [selectedTags, setSelectedTags] =
        useState<Array<{ name: string }>>(initialTags);

    const [minimumVersion, setMinimumVersion] = useState<string | null>(
        post?.minimumVersion ?? null,
    );

    // Keys of FileInputs whose media the Batch worker is still converting.
    const [processingMediaKeys, setProcessingMediaKeys] = useState<
        ReadonlySet<string>
    >(new Set());
    const reportMediaProcessing = useCallback(
        (key: string, isProcessing: boolean) => {
            setProcessingMediaKeys((current) => {
                if (current.has(key) === isProcessing) {
                    return current;
                }
                const next = new Set(current);
                if (isProcessing) {
                    next.add(key);
                } else {
                    next.delete(key);
                }
                return next;
            });
        },
        [],
    );
    const isMediaProcessing = processingMediaKeys.size > 0;
    const isBlockingState = postState === 'in_review' || postState === 'ready';

    const { data: localizationsFromBackend = [] } = usePortalLocalization();
    const [pendingLocalizations, setPendingLocalizations] = useState<
        PortalLocalizationDTO[]
    >([]);

    usePreviewPortalPayload(
        postPayload,
        { leading: true },
        undefined,
        pendingLocalizations,
    );

    const saveLocalization = useSavePortalLocalization();

    const { data: allTags = [], isLoading } = usePortalTags();
    const tagOptions = useMemo(
        () => _.uniqBy([...allTags, ...selectedTags], 'name'),
        [allTags, selectedTags],
    );
    const updateTagsAfterSave = (tags: { name: string }[]) => {
        setInitialTags(tags);
        setSelectedTags(tags);
    };

    const postLocalizationsWithId = pruneStalePostLocalizations(
        _.uniqBy(
            postLocalizations.map((pl) => ({
                ...pl,
                portalPostId: id ?? draftId,
            })),
            (pl) => `${pl.portalPostId}:${pl.path}`,
        ),
        postPayload ?? {},
    );

    const onClickSave = async (templateId: TemplateId) => {
        // Validation runs asynchronously in an effect, so in the blocking
        // states there is a brief window right after an edit where the Save
        // button is still enabled with a stale validation result. Re-validate
        // here so a very recent change can't slip through, and mirror the
        // button's bypass rules (override hotkey / URL flag) so the behaviour
        // stays consistent.
        if (!skipValidationBlock && !isOverrideHeld && isBlockingState) {
            if (isMediaProcessing) {
                return;
            }
            const latestErrors = await validatePostData(
                templateId,
                postPayload,
            );
            setErrors(latestErrors ?? {});
            if (hasErrors(latestErrors)) {
                return;
            }
        }

        const referencedLocalizationIds = new Set(
            postLocalizationsWithId
                .map((pl) => pl.portalLocalizationId)
                .filter((locId): locId is string => Boolean(locId)),
        );
        const prunedPendingLocalizations = pruneOrphanedLocalizations(
            pendingLocalizations,
            referencedLocalizationIds,
        );
        const preparedDraft = {
            payload: postPayload,
            localizations: prunedPendingLocalizations,
            postLocalizations: postLocalizationsWithId,
        };

        saveLocalization.mutate(
            { localizations: preparedDraft.localizations },
            {
                onSuccess: () => {
                    if (id) {
                        updatePost(
                            {
                                postId: id,
                                updatePostDto: {
                                    templateId,
                                    payload: preparedDraft.payload,
                                    state: postState,
                                    notes,
                                    tags: selectedTags,
                                    postLocalizations:
                                        preparedDraft.postLocalizations,
                                    minimumVersion,
                                },
                            },
                            {
                                onSuccess: (post) => {
                                    setDraftTemplateId(post.templateId);
                                    setPostPayload(post.payload);
                                    setInitialPostPayload(post.payload);
                                    updateTagsAfterSave(selectedTags);
                                    setPostLocalizations(
                                        postLocalizationsWithId,
                                    );
                                    setPendingLocalizations([]);
                                },
                                onError: (err) =>
                                    handleError(
                                        'Failed to update the post',
                                        err,
                                    ),
                            },
                        );
                    } else if (draftId) {
                        createPost(
                            {
                                createPostDto: {
                                    id: draftId,
                                    templateId,
                                    payload: preparedDraft.payload,
                                    state: postState,
                                    notes,
                                    tags: selectedTags,
                                    postLocalizations:
                                        preparedDraft.postLocalizations,
                                    minimumVersion,
                                },
                            },
                            {
                                onSuccess: (post) => {
                                    setDraftTemplateId(post.templateId);
                                    setPostPayload(post.payload);
                                    setInitialPostPayload(post.payload);
                                    updateTagsAfterSave(selectedTags);
                                    navigate(RouteTo.PortalPostEdit(post.id), {
                                        replace: true,
                                    });
                                    setPostLocalizations(
                                        postLocalizationsWithId,
                                    );
                                    setPendingLocalizations([]);
                                },
                                onError: (err) =>
                                    handleError(
                                        'Failed to create the post',
                                        err,
                                    ),
                            },
                        );
                    }
                },
                onError: (err) =>
                    handleError('Failed to save localizations', err),
            },
        );
    };

    const hasChanges = useMemo(
        () =>
            !_.isEqual(postPayload, initialPostPayload) ||
            postState !== post?.state ||
            !_.isEqual(selectedTags, initialTags) ||
            notes !== post?.notes ||
            Object.keys(pendingLocalizations).length > 0 ||
            post?.minimumVersion != minimumVersion,
        [
            postPayload,
            initialPostPayload,
            postState,
            post?.state,
            post?.notes,
            post?.minimumVersion,
            selectedTags,
            initialTags,
            notes,
            pendingLocalizations,
            minimumVersion,
        ],
    );
    const localizationRecord = useMemo(() => {
        const map: Record<string, Record<string, string>> = {};
        for (const loc of localizationsFromBackend) {
            if (!map[loc.id]) map[loc.id] = {};
            map[loc.id][loc.language] = loc.translation;
        }

        for (const loc of pendingLocalizations) {
            if (!map[loc.id]) map[loc.id] = {};
            map[loc.id][loc.language] = loc.translation;
        }
        return map;
    }, [localizationsFromBackend, pendingLocalizations]);

    const validations = POST_VALIDATIONS[draftTemplateId];
    const [validationErrors, setErrors] = useState<
        PortalPostEditorContextData['errors']
    >({});
    const [isValidating, setIsValidating] = useState(false);

    // Override hotkey: Ctrl+Shift or Cmd+Shift held to bypass error blocking
    const [isOverrideHeld, setIsOverrideHeld] = useState(false);
    useEffect(() => {
        const update = (e: KeyboardEvent) => {
            setIsOverrideHeld((e.ctrlKey || e.metaKey) && e.shiftKey);
        };
        const reset = () => {
            setIsOverrideHeld(false);
        };
        document.addEventListener('keydown', update);
        document.addEventListener('keyup', update);
        window.addEventListener('blur', reset);
        return () => {
            document.removeEventListener('keydown', update);
            document.removeEventListener('keyup', update);
            window.removeEventListener('blur', reset);
        };
    }, []);

    useEffect(() => {
        let isCancelled = false;
        setIsValidating(true);

        validatePostData(draftTemplateId, postPayload)
            .then((result) => {
                if (!isCancelled) {
                    setErrors(result ?? {});
                }
            })
            .catch((err) => {
                // If validation throws we must not keep showing the previous
                // result — that leaves a stale error frozen on screen even
                // after the user has fixed (or cleared) the field.
                if (!isCancelled) {
                    handleError('Failed to validate the post', err);
                    setErrors({});
                }
            })
            .finally(() => {
                if (!isCancelled) {
                    setIsValidating(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [draftTemplateId, postPayload, selectedTags, pendingLocalizations]);
    useEffect(() => {
        if (post && post.tags && selectedTags.length === 0) {
            setSelectedTags(post.tags.map((pt) => ({ name: pt.name })));
        }
    }, [post?.id]);

    const minimumVersionInvalid =
        minimumVersion && !APP_VER_REGEX.test(minimumVersion);

    return (
        <PortalPostEditorContext.Provider
            value={{
                errors: validationErrors,
                hasChanges,
                post: postPayload,
                validations,
                isEditingExistingPost: Boolean(id),
                postState,
                initialPostState: post?.state,
                postId: id ?? draftId,
                localizations: pendingLocalizations,
                localizationRecord,
                setLocalizations: setPendingLocalizations,
                postLocalizations,
                setPostLocalizations,
            }}
        >
            <Section title="Metadata">
                <PostMetaFields
                    id={id}
                    draftId={draftId}
                    setDraftId={setDraftId}
                    tagOptions={tagOptions}
                    selectedTags={selectedTags}
                    setSelectedTags={setSelectedTags}
                    isLoading={isLoading}
                    postState={postState}
                    setPostState={setPostState}
                    initialPostState={post?.state}
                    minimumVersion={minimumVersion ?? null}
                    onMinimumVersionChange={setMinimumVersion}
                    notes={notes}
                    setNotes={setNotes}
                />
            </Section>
            <Section title="Content">
                <MediaProcessingReporterContext.Provider
                    value={reportMediaProcessing}
                >
                    <PostTemplateInput
                        value={postPayload}
                        templateId={draftTemplateId}
                        onChange={setPostPayload}
                        onTemplateIdChange={(templateId) => {
                            if (templateId !== draftTemplateId) {
                                setDraftTemplateId(templateId);
                                setPostPayload(
                                    POST_TEMPLATES[templateId].emptyData(),
                                );
                            }
                        }}
                    />

                    {id && (
                        <PortalTranslationManager
                            postPayload={postPayload}
                            postId={id}
                            onPostPayloadChange={setPostPayload}
                        />
                    )}

                    <br />

                    <fieldset
                        className={classNames(styles.errorContainer, {
                            [styles.errorContainerNoError]:
                                Object.keys(validationErrors).length === 0,
                            [styles.errorContainerWarningOnly]:
                                Object.keys(validationErrors).length > 0 &&
                                !hasErrors(validationErrors),
                        })}
                    >
                        <legend>Validations</legend>
                        <ValidationErrors errors={validationErrors} />
                    </fieldset>

                    <aside className={styles.saveMenu}>
                        <p>{hasChanges ? 'Unsaved Changes' : 'No Changes'}</p>
                        {isBlockingState && isMediaProcessing && (
                            <p>
                                Media is still processing. Save once it is done,
                                or set the post to In Progress.
                            </p>
                        )}
                        <QAWebToolButton />
                        <ProtectedButton
                            role={UserRoles.PortalWritePost}
                            variant="contained"
                            color={
                                isOverrideHeld && hasErrors(validationErrors)
                                    ? 'warning'
                                    : 'primary'
                            }
                            disabled={
                                minimumVersionInvalid ||
                                !hasChanges ||
                                !draftTemplateId ||
                                !draftId ||
                                !postPayload ||
                                draftId === 'tempPostId' ||
                                draftId.length > PORTAL_POST_ID_MAX_LENGTH ||
                                (!skipValidationBlock &&
                                    !isOverrideHeld &&
                                    isBlockingState &&
                                    (isValidating ||
                                        isMediaProcessing ||
                                        hasErrors(validationErrors)))
                            }
                            loading={props.isSaving}
                            onClick={() => {
                                if (
                                    draftTemplateId &&
                                    postPayload &&
                                    selectedTags
                                ) {
                                    onClickSave(draftTemplateId);
                                }
                            }}
                        >
                            {post ? 'Save' : 'Create'}
                        </ProtectedButton>
                    </aside>
                </MediaProcessingReporterContext.Provider>
            </Section>
        </PortalPostEditorContext.Provider>
    );
}

export const usePortalPostEditor = () => {
    const context = useContext(PortalPostEditorContext);

    assert(
        context !== null,
        'usePortalPost must be used within the context of PortalPostProvider',
    );

    return context;
};

export default Post;
